import {
  bigint,
  integer,
  jsonb,
  numeric,
  pgTable,
  text,
  timestamp,
  uuid,
  index,
  uniqueIndex,
  check,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";

// ---------------------------------------------------------------------------
// Touchpoint Event Types — Full Funnel
// ---------------------------------------------------------------------------
// Acquisition (anonymous): page.landed, ad.clicked, affiliate.clicked,
//   referral.converted, qr.scanned, social.clicked, search.organic, search.paid
// Engagement (identified): email/sms/whatsapp interactions, form, booking, workflow
// ---------------------------------------------------------------------------

export const TOUCHPOINT_EVENT_TYPES = [
  // Acquisition layer (anonymous → identified)
  "page.landed", "ad.clicked", "affiliate.clicked", "referral.converted",
  "qr.scanned", "social.clicked", "search.organic", "search.paid",
  // Engagement layer (identified)
  "email.sent", "email.opened", "email.clicked",
  "sms.sent", "sms.delivered", "sms.clicked",
  "whatsapp.sent", "whatsapp.delivered", "whatsapp.read",
  "form.submitted", "booking.started", "page.viewed",
  "workflow.enrolled", "campaign.sent",
  // Reputation layer
  "review.clicked",
  // Course layer
  "course.enrolled", "course.completed", "course.lesson.completed",
  "course.quiz.passed", "course.quiz.failed",
  // Social layer
  "social.post.clicked", "social.comment.received", "social.dm.received",
] as const;

export type TouchpointEventType = (typeof TOUCHPOINT_EVENT_TYPES)[number];

// ---------------------------------------------------------------------------
// Conversion Event Types
// ---------------------------------------------------------------------------

export const CONVERSION_EVENT_TYPES = [
  "payment.completed", "deal.won", "booking.confirmed", "form.goal.reached", "custom",
] as const;

export type ConversionEventType = (typeof CONVERSION_EVENT_TYPES)[number];

// ---------------------------------------------------------------------------
// Attribution Models
// ---------------------------------------------------------------------------

export const ATTRIBUTION_MODEL_TYPES = [
  "firstTouch", "lastTouch", "linear", "timeDecay", "positionBased",
] as const;

export type AttributionModelType = (typeof ATTRIBUTION_MODEL_TYPES)[number];

/** Credit assigned to a single touchpoint within an attribution model */
export interface AttributionCredit {
  touchpointId: string;
  eventType: string;
  channel: string | null;
  credit: number; // 0.0–1.0, all credits for a model sum to 1.0
  touchedAt: string;
}

/** All 5 models stored per conversion */
export interface AttributionModelResults {
  firstTouch: AttributionCredit[];
  lastTouch: AttributionCredit[];
  linear: AttributionCredit[];
  timeDecay: AttributionCredit[];
  positionBased: AttributionCredit[];
}

// ---------------------------------------------------------------------------
// Touchpoint Metadata Contracts
// ---------------------------------------------------------------------------

/** Metadata for acquisition-layer touchpoints (page.landed, ad.clicked, etc.) */
export interface AcquisitionTouchpointMetadata {
  landingUrl?: string;
  referrer?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  /**
   * ADS-009 Phase 3 — GA4 `utm_id` canonical campaign identifier mirrored on
   * the `attribution_touchpoints.utm_id` first-class column. Kept in metadata
   * for legacy reads + audit; the indexed column is the join key.
   */
  utmId?: string;
  affiliateCode?: string;
  gclid?: string;
  fbclid?: string;
  countryCode?: string;
  deviceType?: string;
  browser?: string;
}

/** Metadata for engagement-layer touchpoints (email.opened, sms.clicked, etc.) */
export interface EngagementTouchpointMetadata {
  subject?: string;
  linkUrl?: string;
  templateId?: string;
}

// ---------------------------------------------------------------------------
// Attribution Touchpoints — one row per marketing interaction (full funnel)
// ---------------------------------------------------------------------------

export const attributionTouchpoints = pgTable("attribution_touchpoints", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  contactId: uuid("contact_id"),  // nullable for anonymous touchpoints
  anonymousId: text("anonymous_id"),  // browser cookie/fingerprint for pre-identification
  eventType: text("event_type").notNull(),
  campaignId: uuid("campaign_id"),
  workflowId: uuid("workflow_id"),
  workflowEnrollmentId: uuid("workflow_enrollment_id"),
  messageId: text("message_id"),
  channel: text("channel"),
  countryCode: text("country_code"),  // ISO 3166-1 alpha-2 from CF-IPCountry
  /**
   * ADS-009 Phase 3 — canonical GA4 `utm_id` campaign identifier. First-class
   * column so the Phase 4 ROI pipeline can join touchpoint → session → form
   * → deal → payment on a stable key even when `utm_source` / `utm_medium`
   * casing drift between caller surfaces (ads/social/email/qr/page_builder).
   * Nullable because legacy rows + organic touchpoints have no campaign id.
   */
  utmId: text("utm_id"),
  touchedAt: timestamp("touched_at", { withTimezone: true }).notNull(),
  metadata: jsonb("metadata").$type<AcquisitionTouchpointMetadata | EngagementTouchpointMetadata>().default({}).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  index("idx_attr_tp_contact_touched").on(table.tenantId, table.contactId, table.touchedAt),
  index("idx_attr_tp_campaign").on(table.tenantId, table.campaignId, table.touchedAt),
  index("idx_attr_tp_workflow").on(table.tenantId, table.workflowId, table.touchedAt),
  index("idx_attr_tp_anonymous").on(table.tenantId, table.anonymousId),
  index("idx_attr_tp_country").on(table.tenantId, table.countryCode, table.touchedAt),
  // ADS-009 Phase 3 — supports `WHERE tenant_id = $1 AND utm_id = $2` joins
  // in the Phase 4 ROI pipeline.
  index("idx_attr_tp_utm_id").on(table.tenantId, table.utmId),
]);

// ---------------------------------------------------------------------------
// Attribution Conversions — one row per conversion event with 5 pre-computed models
// ---------------------------------------------------------------------------

export const attributionConversions = pgTable("attribution_conversions", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  contactId: uuid("contact_id").notNull(),
  conversionEventType: text("conversion_event_type").notNull(),
  conversionEventId: text("conversion_event_id").notNull(),
  dealId: uuid("deal_id"), // CRM deal linked to this conversion (nullable — not all conversions are deal-based)
  valueCents: integer("value_cents"),
  currency: text("currency").notNull().default("EUR"),
  lookbackWindowDays: integer("lookback_window_days").notNull().default(30),
  touchpointCount: integer("touchpoint_count").notNull().default(0),
  attributionModels: jsonb("attribution_models").$type<AttributionModelResults>().default({
    firstTouch: [], lastTouch: [], linear: [], timeDecay: [], positionBased: [],
  }).notNull(),
  convertedAt: timestamp("converted_at", { withTimezone: true }).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  index("idx_attr_conv_contact").on(table.tenantId, table.contactId, table.convertedAt),
  index("idx_attr_conv_tenant_converted").on(table.tenantId, table.convertedAt),
  uniqueIndex("uq_attr_conv_event").on(table.tenantId, table.conversionEventType, table.conversionEventId),
  index("idx_attr_conv_deal").on(table.tenantId, table.dealId),
]);

// ---------------------------------------------------------------------------
// Affiliates — per-tenant affiliate tracking
// ---------------------------------------------------------------------------

export const AFFILIATE_STATUSES = ["active", "paused", "terminated"] as const;
export type AffiliateStatus = (typeof AFFILIATE_STATUSES)[number];

export const affiliates = pgTable("affiliates", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  userId: text("user_id"),
  code: text("code").notNull(),
  name: text("name").notNull(),
  email: text("email"),
  commissionPercent: numeric("commission_percent", { precision: 5, scale: 2 }).notNull().default("0"),
  status: text("status").notNull().default("active"),
  totalReferrals: integer("total_referrals").notNull().default(0),
  totalConversions: integer("total_conversions").notNull().default(0),
  totalRevenueCents: bigint("total_revenue_cents", { mode: "number" }).notNull().default(0),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  uniqueIndex("uq_affiliates_tenant_code").on(table.tenantId, table.code),
  index("idx_affiliates_tenant").on(table.tenantId),
  index("idx_affiliates_user").on(table.tenantId, table.userId),
]);

// ---------------------------------------------------------------------------
// Affiliate Payouts — monthly payout lifecycle (D6)
// ---------------------------------------------------------------------------

export const PAYOUT_STATUSES = ["pending", "approved", "processing", "paid", "rejected"] as const;
export type PayoutStatus = (typeof PAYOUT_STATUSES)[number];

export const affiliatePayouts = pgTable("affiliate_payouts", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  affiliateId: uuid("affiliate_id").notNull(),
  periodStart: timestamp("period_start", { withTimezone: true }).notNull(),
  periodEnd: timestamp("period_end", { withTimezone: true }).notNull(),
  commissionCents: integer("commission_cents").notNull(),
  currency: text("currency").notNull().default("eur"),
  conversionsCount: integer("conversions_count").notNull().default(0),
  status: text("status").notNull().default("pending"),
  approvedBy: text("approved_by"),
  approvedAt: timestamp("approved_at", { withTimezone: true }),
  paymentReference: text("payment_reference"),
  paidAt: timestamp("paid_at", { withTimezone: true }),
  rejectionReason: text("rejection_reason"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  index("idx_affiliate_payouts_tenant").on(table.tenantId),
  index("idx_affiliate_payouts_affiliate").on(table.affiliateId),
  index("idx_affiliate_payouts_status").on(table.status),
  index("idx_affiliate_payouts_period").on(table.tenantId, table.periodStart),
  check(
    "chk_affiliate_payout_status",
    sql`${table.status} IN ('pending', 'approved', 'processing', 'paid', 'rejected')`,
  ),
]);
